import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Typography,
  message,
} from "antd";
import { SYMBOL_TYPES } from "../../FloorPlanModal/utils/symbolTypes";
import { IndoorPositioningEquipment } from "../../../../api";
import {
  resolveDisplayTopic,
  isValidBuzzerTopicFormat,
} from "../utils/buzzerTopic";
import "./EquipmentFormModal.css";

const { Option } = Select;

// 機具圖示只提供標記為機具（isEquipment）的符號，
// 排除同屬「設備」分類但並非機具的水源、電力設施等符號。
const equipmentSymbolOptions = Object.values(SYMBOL_TYPES).filter(
  (symbol) => symbol.isEquipment,
);

const EquipmentFormModal = ({
  open,
  editingRecord,
  machineID,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  // Hooks must be called unconditionally (Rules of Hooks) even though this
  // value is only rendered when editingRecord?.id is set -- see the JSX
  // below for the create-mode / invalid-topic gating.
  const typedBuzzerTopic = Form.useWatch("buzzer_mqtt_topic", form);

  useEffect(() => {
    if (!open) return;
    if (editingRecord) {
      form.setFieldsValue({
        name: editingRecord.name,
        symbol_type: editingRecord.symbol_type,
        alert_radius_meters: editingRecord.alert_radius_meters ?? 5,
        span_meters: editingRecord.span_meters ?? undefined,
        buzzer_mqtt_topic: editingRecord.buzzer_mqtt_topic ?? undefined,
        is_active: editingRecord.is_active ?? true,
        alert_enabled: editingRecord.alert_enabled ?? true,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        alert_radius_meters: 5,
        is_active: true,
        alert_enabled: true,
      });
    }
  }, [open, editingRecord, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const trimmedBuzzerTopic = values.buzzer_mqtt_topic
        ? values.buzzer_mqtt_topic.trim()
        : "";
      const payload = {
        name: values.name,
        symbol_type: values.symbol_type,
        alert_radius_meters: values.alert_radius_meters,
        span_meters: values.span_meters ?? null,
        buzzer_mqtt_topic: trimmedBuzzerTopic || null,
        is_active: values.is_active,
        alert_enabled: values.alert_enabled,
      };

      setSubmitting(true);
      const response = editingRecord
        ? await IndoorPositioningEquipment.UpdateEquipment(
            editingRecord.id,
            payload,
          )
        : await IndoorPositioningEquipment.CreateEquipment(machineID, payload);

      if (response.code === 200) {
        message.success(editingRecord ? "更新機具成功" : "新增機具成功");
        form.resetFields();
        onSaved();
      } else {
        message.error(
          response.message || (editingRecord ? "更新機具失敗" : "新增機具失敗"),
        );
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error("提交機具失敗:", error);
      message.error(editingRecord ? "更新機具失敗" : "新增機具失敗");
    } finally {
      setSubmitting(false);
    }
  };

  // 選擇機具圖示時，若機具名稱尚未填寫（含全空白），則自動帶入該圖示的預設名稱，
  // 方便使用者快速建立機具；已有名稱時（包含本功能先前帶入的名稱）一律不覆寫。
  const handleSymbolTypeChange = (value) => {
    const currentName = form.getFieldValue("name");
    if (currentName && currentName.trim() !== "") return;

    const selectedSymbol = equipmentSymbolOptions.find(
      (symbol) => symbol.id === value,
    );
    if (!selectedSymbol) return;

    // 用 setFieldValue（單數）而非 setFieldsValue：rc-field-form 的
    // setFieldValue 內部會連同 errors/warnings 一併清空，因此若使用者先按過
    // 確認看到「請輸入機具名稱」的錯誤，帶入名稱後錯誤提示也會一併消失；
    // setFieldsValue 只更新值，不會清掉既有的驗證錯誤狀態。
    form.setFieldValue("name", selectedSymbol.name);
  };

  return (
    <>
      <Modal
        title={editingRecord ? "編輯機具" : "新增機具"}
        open={open}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText="確認"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="機具圖示"
            name="symbol_type"
            rules={[{ required: true, message: "請選擇機具圖示" }]}
          >
            <Select
              placeholder="請選擇機具圖示"
              onChange={handleSymbolTypeChange}
            >
              {equipmentSymbolOptions.map((symbol) => {
                const Icon = symbol.icon;
                return (
                  <Option key={symbol.id} value={symbol.id}>
                    <Icon style={{ marginRight: 6, color: symbol.color }} />
                    {symbol.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label="機具名稱"
            name="name"
            rules={[{ required: true, message: "請輸入機具名稱" }]}
          >
            <Input placeholder="請輸入機具名稱（例如：挖土機A）" />
          </Form.Item>
          <Form.Item
            label="警戒半徑 (公尺)"
            name="alert_radius_meters"
            tooltip="警戒半徑是以機具「車頭↔車尾」的連線為基準向外量測的緩衝距離，而非單一中心點，與防護區的量測方式不同。"
            rules={[{ required: true, message: "請輸入警戒半徑" }]}
          >
            <InputNumber
              min={0.5}
              max={100}
              step={0.5}
              style={{ width: "100%" }}
              addonAfter="m"
            />
          </Form.Item>
          <Form.Item
            label="機具長度 (公尺)"
            name="span_meters"
            tooltip="車頭與車尾定位器之間的固定距離，用來避免機具在即時畫面上跟著訊號誤差伸縮變形，一律套用於車頭/車尾兩點的連線。若不確定可留空。"
            rules={[
              {
                validator: (_, value) => {
                  if (value == null || value === "") return Promise.resolve();
                  if (value < 0.5 || value > 100) {
                    return Promise.reject("機具長度需介於 0.5 至 100 公尺");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0.5}
              max={100}
              step={0.5}
              style={{ width: "100%" }}
              addonAfter="m"
              placeholder="留空表示不做長度校正"
            />
          </Form.Item>
          <Form.Item
            label="蜂鳴器 MQTT 主題"
            name="buzzer_mqtt_topic"
            tooltip="留空則使用預設主題 indoor_positioning/<機具編號>/equipment/<機具ID>/alarm"
            rules={[
              {
                validator: (_, value) => {
                  if (isValidBuzzerTopicFormat(value)) return Promise.resolve();
                  const trimmed = String(value).trim();
                  if (trimmed.length > 255) {
                    return Promise.reject(
                      "蜂鳴器 MQTT 主題長度不可超過 255 字元",
                    );
                  }
                  if (trimmed.includes("+") || trimmed.includes("#")) {
                    return Promise.reject(
                      "蜂鳴器 MQTT 主題不可包含萬用字元 + 或 #",
                    );
                  }
                  return Promise.reject("蜂鳴器 MQTT 主題不可以 $ 開頭");
                },
              },
            ]}
          >
            <Input placeholder="留空則使用預設主題 indoor_positioning/<機具編號>/equipment/<機具ID>/alarm" />
          </Form.Item>
          {editingRecord?.id ? (
            isValidBuzzerTopicFormat(typedBuzzerTopic) &&
            (() => {
              const { topic } = resolveDisplayTopic({
                typedTopic: typedBuzzerTopic,
                defaultTopic: editingRecord.buzzer_topic_default,
              });
              if (!topic) return null;
              return (
                <div
                  className="text-muted small buzzer-topic-line"
                  style={{ marginTop: -12, marginBottom: 16 }}
                >
                  目前生效主題{" "}
                  <Typography.Text code>{topic}</Typography.Text>{" "}
                  {/* copyable lives on its OWN Text, not on the `code` one:
                      antd renders the copy icon as a CHILD of the element it
                      is set on, which would put the button inside the grey
                      <code> box together with the topic string. Kept as a
                      sibling so the box contains only the topic itself.
                      Still antd's copyable (never navigator.clipboard) --
                      prod is served over plain http, where that API does not
                      exist; antd falls back to execCommand. */}
                  <Typography.Text
                    copyable={{ text: topic, tooltips: ["複製", "已複製"] }}
                  />
                </div>
              );
            })()
          ) : (
            <div
              className="text-muted small"
              style={{ marginTop: -12, marginBottom: 16 }}
            >
              儲存後產生
            </div>
          )}
          <Form.Item
            label="使用中"
            name="is_active"
            valuePropName="checked"
            initialValue={true}
            tooltip="停用後，此機具會從即時畫布上完全消失且不再警報，但設定與定位器資料都會保留，可隨時重新啟用。"
          >
            <Switch checkedChildren="開" unCheckedChildren="關" />
          </Form.Item>
          <Form.Item
            label="警報啟用"
            name="alert_enabled"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="開" unCheckedChildren="關" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EquipmentFormModal;
